import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 실시간 지도 마커 보간 훅 — 속도 기반 적응형 lerp + dead reckoning.
 *
 * ────────────────────────────────────────────────────────────────
 * 🧭 동작 모델
 * ────────────────────────────────────────────────────────────────
 *  매 서버 프레임마다:
 *   1. dt, 거리(경도 cos 보정), 순간속도 계산
 *   2. 이상치(>200km/h) 판정 → 예측 제외
 *   3. 속도 스칼라 EMA (α=0.4), 간격 EMA (α=0.3) 갱신
 *   4. 속도 벡터 — 최근 3 샘플 이동평균 (EMA 대비 노이즈 내성 ↑, 방향 일관성 ↑)
 *   5. 속도 모드별 duration 선택
 *      정지(<0.5m/s) 400ms · 도보(<2.5) 600ms · 이동(≥2.5) min(interval*0.9, 4500)
 *   6. Dead reckoning — 비이상치 & 비정지 일 때:
 *      extTarget = rx + velocity * lookahead
 *      lookahead = min(duration, 1500ms, interval*0.9)
 *   7. (startPos=현재 렌더 좌표, targetPos=extTarget, startTime, duration) 교체.
 *      방향 전환 시에도 startPos 가 현재 render 위치이므로 튀지 않고 자연 스럽게 휨.
 *
 *  매 프레임 (RAF, ~30fps setState 쓰로틀):
 *   · elapsed ≤ duration: start→target 선형 보간
 *   · elapsed > duration: target 에서 EMA 속도로 연속 dead reckoning
 *                        (cap=3s, 다음 서버 프레임까지 정지 없이 흘림)
 *
 *  결과:
 *   · 도보 2km/h 도 끊김 없이 연속 이동 — duration 짧아도 extrapolation 으로 이어감
 *   · 주행 — 서버 간격에 맞춰 부드럽게, dead reckoning 으로 실제 위치 근접
 *   · 방향 전환 — 새 target 로 향하는 lerp 가 current render 에서 출발 → 자연스러운 곡선
 *   · 정지 — 3-샘플 평균이 금방 0 에 수렴 → extrapolation 멈춤
 *   · 이상치 — 즉시 스냅, 속도 history 리셋 (잘못된 방향 예측 방지)
 */

const EMA_ALPHA = 0.3;
const SPEED_EMA_ALPHA = 0.4; // 속도 스칼라는 빠르게 반응 (모드 전환 대응)
// 속도 벡터는 EMA 대신 3-샘플 이동평균 사용 (VEL_HISTORY_SIZE 참고) — 단일 프레임 튀김에 덜 흔들림.
const DEFAULT_INTERVAL_MS = 5000;
const MIN_INTERVAL_MS = 500;
const RENDER_INTERVAL_MS = 16; // ~60fps — 30fps 는 느린 이동에서 프레임 단위 계단감 발생

// ─────────────────────────────────────────────────────────────
// Dead Reckoning (예측 보간) 파라미터
// 서버 좌표를 그대로 쫓아가면 평균 interval/2 만큼 지연 → 속도 벡터로
// 한발 앞 지점을 target 으로 잡아 "현재 실제 위치" 에 가깝게 렌더.
// ─────────────────────────────────────────────────────────────
// Lookahead 개념 제거 — target = 수신 좌표 그대로. 이유:
//  · 예측은 사용자가 일정 속도로 이동할 때만 정확하고, 정지/방향전환 시 오버슈트
//  · "관찰된 속도로 실시간 재생" 방식이 더 자연스러운 UX (내비 앱/친구찾기와 동일)
//  · 결과: render 는 항상 직전 프레임까지의 실제 경로를 재생, 예측 없음
// 세그먼트(duration) 종료 후 연속 dead reckoning 상한 배수 — avgInterval 기반 동적.
// 예) interval 5s → cap 6s, interval 15s → cap 18s.
// 다음 서버 프레임이 도착할 때까지 가운데 공백을 메워 정지 감 제거.
// 절대 상한은 EXTRAP_ABSOLUTE_CAP_MS 로 오버슈트 폭주 방지.
const EXTRAP_INTERVAL_RATIO = 1.2;
const EXTRAP_ABSOLUTE_CAP_MS = 20000; // 20s — 프레임 드랍 / 롱 인터벌 대응

// ─────────────────────────────────────────────────────────────
// 속도 기반 적응형 보간 파라미터
// 실측 속도(m/s)별로 duration 을 달리 잡아 "걷기=즉응 / 차량=연속 이동"
// ─────────────────────────────────────────────────────────────
const STATIONARY_SPEED_MPS = 0.5;  // GPS 떨림 수준 (이하 + 거리 <5m 는 정지로 판정)

const STATIONARY_ANIM_MS = 400;    // 정지 시 — 작은 떨림 부드럽게
const WALKING_ANIM_MS = 600;       // duration 하한 — 너무 짧으면 스냅처럼 보여 이징감 상실
// duration 상한 — 관찰 dt 가 이 값을 넘으면 상한으로 clamp (응답성 보장).
// 20s 는 걸음/간헐 GPS(10~20s 간격) 를 자연스럽게 커버하는 실용 상한.
const DRIVING_MAX_ANIM_MS = 20000;

// GPS 떨림 흡수 — 이 거리 이하 & 저속이면 사실상 제자리
const STATIONARY_DISTANCE_M = 5;

// 비현실적 점프 (200km/h 초과) = GPS 이상치 → 즉시 스냅 후 속도 EMA 제외
const OUTLIER_SPEED_MPS = 55;
const OUTLIER_SNAP_MS = 150;

// 위경도↔미터 변환 — 경도는 위도에 따라 cos 보정해야 동서 거리가 정확.
const DEG_TO_METERS = 111000;

const calcDistanceMeters = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const midLatRad = ((lat1 + lat2) / 2) * (Math.PI / 180);
    const cosLat = Math.cos(midLatRad);
    const dLat = (lat2 - lat1) * DEG_TO_METERS;
    const dLng = (lng2 - lng1) * DEG_TO_METERS * cosLat;
    return Math.sqrt(dLat * dLat + dLng * dLng);
};

const pickDuration = (
    speedMps: number,
    distMeters: number,
    dtMs: number,
    isOutlier: boolean,
): number => {
    if (isOutlier) return OUTLIER_SNAP_MS;
    if (speedMps < STATIONARY_SPEED_MPS && distMeters < STATIONARY_DISTANCE_M) {
        return STATIONARY_ANIM_MS;
    }
    // 관찰된 이번 프레임 dt 를 그대로 duration 으로 사용.
    // → 화면 시각 속도 = 실제 관찰 속도(distance/dt) 매칭. 과속/저속 왜곡 없음.
    // 단 너무 짧으면 이징감이 사라지고, 너무 길면 응답성 저하 → 상/하한 clamp.
    return Math.max(WALKING_ANIM_MS, Math.min(dtMs, DRIVING_MAX_ANIM_MS));
};

interface MarkerMotion {
    startLat: number;
    startLng: number;
    targetLat: number;       // 예측 extrapolation 적용된 target (lerp 도착지점)
    targetLng: number;
    startTime: number;
    avgInterval: number;     // EMA 추정 업데이트 간격 (ms)
    avgSpeedMps: number;     // EMA 추정 속도 (m/s) — 모드 분류용
    vLatPerMs: number;       // 최근 3 샘플 평균 속도 벡터 (deg/ms) — dead reckoning 용
    vLngPerMs: number;
    velHistory: { vLat: number; vLng: number }[]; // 순환 3 샘플 — 이동평균 원본
    duration: number;        // 현재 세그먼트 보간 길이 (ms)
    renderLat: number;       // 현재 화면 좌표 (최신 프레임 기준)
    renderLng: number;
    lastRxLat: number;       // 마지막 수신 원본 좌표 (속도/거리 계산용)
    lastRxLng: number;
    lastUpdate: number;      // 마지막 updateMarker 호출 시각
}

const VEL_HISTORY_SIZE = 3; // 이동평균 샘플 수 — 3~4 가 속도 안정성/반응성 균형

export interface SmoothMarker {
    lat: number;
    lng: number;
    updatedAt: string;
}

export type SmoothMarkerMap = Record<number, SmoothMarker>;

export interface UseSmoothMarkersResult {
    markers: SmoothMarkerMap;
    updateMarker: (userNo: number, lat: number, lng: number) => void;
    removeMarker: (userNo: number) => void;
    resetMarker: (userNo: number, lat: number, lng: number) => void;
}

export default function useSmoothMarkers(): UseSmoothMarkersResult {
    const [markers, setMarkers] = useState<SmoothMarkerMap>({});
    const statesRef = useRef<Record<number, MarkerMotion>>({});
    const rafRef = useRef<number | null>(null);
    const lastFrameRef = useRef<number>(Date.now());
    const renderAccumRef = useRef<number>(0);

    const updateMarker = useCallback((userNo: number, lat: number, lng: number) => {
        const now = Date.now();
        const prev = statesRef.current[userNo];

        if (!prev) {
            statesRef.current[userNo] = {
                startLat: lat, startLng: lng,
                targetLat: lat, targetLng: lng,
                renderLat: lat, renderLng: lng,
                lastRxLat: lat, lastRxLng: lng,
                startTime: now,
                avgInterval: DEFAULT_INTERVAL_MS,
                avgSpeedMps: 0,
                vLatPerMs: 0,
                vLngPerMs: 0,
                velHistory: [],
                duration: WALKING_ANIM_MS, // 최초 프레임은 속도 미지 → 보행 기본값
                lastUpdate: now,
            };
            setMarkers(m => ({
                ...m,
                [userNo]: { lat, lng, updatedAt: new Date(now).toISOString() },
            }));
            return;
        }

        // 실제 이번 업데이트 간격 (EMA 갱신용)
        const dt = Math.max(MIN_INTERVAL_MS, now - prev.lastUpdate);
        const newAvg = (1 - EMA_ALPHA) * prev.avgInterval + EMA_ALPHA * dt;

        // 실제 수신 좌표 기준 이동거리 (경도 cos 보정 포함) — 속도/모드 판정 근거
        const distMeters = calcDistanceMeters(prev.lastRxLat, prev.lastRxLng, lat, lng);
        const instantSpeed = distMeters / (dt / 1000);
        // GPS 이상치: 200km/h 초과 점프 → 예측에서 제외, 스냅, 속도 리셋
        const isOutlier = instantSpeed > OUTLIER_SPEED_MPS;
        // 속도 EMA (이상치는 제외) — 모드 분류용 스칼라
        const newSpeed = isOutlier
            ? prev.avgSpeedMps
            : (1 - SPEED_EMA_ALPHA) * prev.avgSpeedMps + SPEED_EMA_ALPHA * instantSpeed;

        // 속도 벡터 — 최근 3 샘플 이동평균 (deg/ms).
        // EMA 대비 단일 프레임 노이즈에 덜 흔들리고 방향 일관성 ↑.
        const instantVLat = (lat - prev.lastRxLat) / dt;
        const instantVLng = (lng - prev.lastRxLng) / dt;
        if (isOutlier) {
            // 이상치 프레임은 history 오염 방지 위해 리셋
            prev.velHistory = [];
        } else {
            prev.velHistory.push({ vLat: instantVLat, vLng: instantVLng });
            while (prev.velHistory.length > VEL_HISTORY_SIZE) prev.velHistory.shift();
        }
        const samples = prev.velHistory;
        const newVLat = samples.length > 0 ? samples.reduce((s, v) => s + v.vLat, 0) / samples.length : 0;
        const newVLng = samples.length > 0 ? samples.reduce((s, v) => s + v.vLng, 0) / samples.length : 0;

        // duration — 이번 프레임 실제 dt 를 기반으로 "관찰된 속도" 로 이동시킴.
        // lookahead 없이 target = 수신 좌표 그대로 → 오버슈트 없음, 방향 전환 자연스러움.
        const duration = pickDuration(newSpeed, distMeters, dt, isOutlier);

        // 현재 화면 좌표부터 수신 좌표 까지 lerp.
        // 방향이 바뀐 새 target 이 와도 start = 현재 renderLat (extrapolation 으로 진행 중이던 위치) →
        // 해당 위치에서 새 방향으로 자연스럽게 휘어 진입. 튀는 점프 없음.
        prev.startLat = prev.renderLat;
        prev.startLng = prev.renderLng;
        prev.targetLat = lat;
        prev.targetLng = lng;
        prev.startTime = now;
        prev.avgInterval = newAvg;
        prev.avgSpeedMps = newSpeed;
        prev.vLatPerMs = newVLat;
        prev.vLngPerMs = newVLng;
        prev.duration = duration;
        prev.lastRxLat = lat;
        prev.lastRxLng = lng;
        prev.lastUpdate = now;
    }, []);

    const resetMarker = useCallback((userNo: number, lat: number, lng: number) => {
        const now = Date.now();
        statesRef.current[userNo] = {
            startLat: lat, startLng: lng,
            targetLat: lat, targetLng: lng,
            renderLat: lat, renderLng: lng,
            lastRxLat: lat, lastRxLng: lng,
            startTime: now,
            avgInterval: DEFAULT_INTERVAL_MS,
            avgSpeedMps: 0,
            vLatPerMs: 0,
            vLngPerMs: 0,
            velHistory: [],
            duration: WALKING_ANIM_MS,
            lastUpdate: now,
        };
        setMarkers(m => ({
            ...m,
            [userNo]: { lat, lng, updatedAt: new Date(now).toISOString() },
        }));
    }, []);

    const removeMarker = useCallback((userNo: number) => {
        delete statesRef.current[userNo];
        setMarkers(m => {
            const next = { ...m };
            delete next[userNo];
            return next;
        });
    }, []);

    useEffect(() => {
        const tick = () => {
            const now = Date.now();
            const frameDt = Math.min(100, now - lastFrameRef.current);
            lastFrameRef.current = now;
            renderAccumRef.current += frameDt;

            const userNos = Object.keys(statesRef.current);
            if (userNos.length > 0) {
                userNos.forEach(key => {
                    const userNo = Number(key);
                    const s = statesRef.current[userNo];
                    if (!s) return;

                    const elapsed = now - s.startTime;
                    if (elapsed <= s.duration || s.duration === 0) {
                        // ── 세그먼트 구간: start → target 선형 보간 ──
                        // 새 프레임이 오면 updateMarker 가 start = 현재 renderLat 로 잡아줘
                        // 방향이 바뀌어도 현재 위치에서 부드럽게 휘어 진입.
                        const progress = s.duration > 0 ? elapsed / s.duration : 1;
                        s.renderLat = s.startLat + (s.targetLat - s.startLat) * progress;
                        s.renderLng = s.startLng + (s.targetLng - s.startLng) * progress;
                    } else {
                        // ── 세그먼트 종료 후: 3-샘플 평균 속도로 연속 dead reckoning ──
                        // duration 끝에 target 에 도달하면 정지하지 않고 v*t 만큼 계속 이동.
                        // cap 을 avgInterval 기반 으로 동적 계산 — 간격이 길수록(보행/간헐적 GPS)
                        // extrapolation 도 길게 허용해야 "5~15s 긴 공백에서 멈춤" 현상 사라짐.
                        const cap = Math.min(s.avgInterval * EXTRAP_INTERVAL_RATIO, EXTRAP_ABSOLUTE_CAP_MS);
                        const over = Math.min(elapsed - s.duration, cap);
                        s.renderLat = s.targetLat + s.vLatPerMs * over;
                        s.renderLng = s.targetLng + s.vLngPerMs * over;
                    }
                });

                // 30fps 로 setState 쓰로틀
                if (renderAccumRef.current >= RENDER_INTERVAL_MS) {
                    renderAccumRef.current = 0;
                    const snapshot: SmoothMarkerMap = {};
                    const iso = new Date(now).toISOString();
                    userNos.forEach(key => {
                        const userNo = Number(key);
                        const s = statesRef.current[userNo];
                        if (s) snapshot[userNo] = {
                            lat: s.renderLat,
                            lng: s.renderLng,
                            updatedAt: iso,
                        };
                    });
                    setMarkers(snapshot);
                }
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        lastFrameRef.current = Date.now();
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, []);

    return { markers, updateMarker, removeMarker, resetMarker };
}
